import { ChangeEvent } from 'react';
import { observer } from 'mobx-react';
import ButtonBlock from '../ButtonBlock/ButtonBlock';
import { LOCALES } from 'src/constants/main';
import { localeStore } from 'src/mobx/localesStore';
import Button from 'src/view/components/Controls/ButtonComponent/Button';
import { messageCampaignStore } from '~/view/screens/MarketingScreen/screens/MessageCampaign/CampaignEditScreen/store/messageCampaignStore';
import { confirmDelete } from '~/utils/modalConfirmations';
import { history } from '~/services/history';
import { SCREENS } from '~/constants/screens';
import CampaignContent from '~/view/screens/MarketingScreen/screens/MessageCampaign/CampaignEditScreen/components/MessageCampaignForm/CampaignContent/CampaignContent';
import CampaignDeliveryMethod from '~/view/screens/MarketingScreen/screens/MessageCampaign/CampaignEditScreen/components/MessageCampaignForm/CampaignDeliveryMethod';
import MessageCampaignScheduled from '~/view/screens/MarketingScreen/screens/MessageCampaign/CampaignEditScreen/components/MessageCampaignForm/MessageCampaignScheduled/MessageCampaignScheduled';

const MessageCampaignForm: React.FC = observer(() => {
  const { message, subject, language } = messageCampaignStore.form;

  const changeTextDirection = () => {
    if (language === LOCALES.EN) {
      messageCampaignStore.changeForm({ language: LOCALES.AR });
    } else {
      messageCampaignStore.changeForm({ language: LOCALES.EN });
    }
  };

  const subjectChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    messageCampaignStore.changeForm({ subject: event.target.value });
    if (event.target.value.length && !messageCampaignStore.validationErrors.subject.isValid) {
      messageCampaignStore.validateSubject();
    }
  };

  const messageChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    messageCampaignStore.changeForm({ message: event.target.value });
    if (event.target.value.length && !messageCampaignStore.validationErrors.message.isValid) {
      messageCampaignStore.validateMessage();
    }
  };

  const addSmile = (smile: string) => {
    messageCampaignStore.changeForm({ message: message + smile });
    messageCampaignStore.validateSubject();
  };

  const handleDelete = () => {
    confirmDelete({
      title: localeStore.t('marketing_mc_delete_comfirmation_header'),
      message: localeStore.t('marketing_mc_delete_comfirmation_bodytext'),
      onConfirm: async () => {
        await messageCampaignStore.deleteCampaign(messageCampaignStore.form.id || 0);
        history.push(SCREENS.MESSAGE_CAMPAIGNS());
      },
    });
  };

  const handleSendCampaign = async () => {
    messageCampaignStore.validateForm();

    const isValidForm = Object.values(messageCampaignStore.validationErrors).every((field) => field.isValid);
    if (!isValidForm) return;

    if (messageCampaignStore.form.id) {
      await messageCampaignStore.updateCampaign();
    } else {
      await messageCampaignStore.createCampaign();
    }

    history.push(SCREENS.MESSAGE_CAMPAIGNS());
  };

  return (
    <div className="d-flex flex-column full-height justify-content-between mr-4">
      <CampaignDeliveryMethod />

      <div className="d-flex flex-column full-height full-width align-items-center mt-10">
        <div className="pos-relative full-width mb-9">
          <CampaignContent
            text={subject}
            language={language}
            placeholder={localeStore.t('hint_subject') + ' *'}
            hint="marketing_mc_subject_max_length_message"
            maxChars={65}
            isValid={messageCampaignStore.validationErrors.subject.isValid}
            className="family-bold text-size-26"
            onChange={subjectChange}
          />
        </div>
        <div className="d-flex flex-row full-width  h-60">
          <div className="pos-relative full-width full-height">
            <CampaignContent
              text={message}
              language={language}
              placeholder={localeStore.t('hint_message')}
              hint="marketing_mc_text_max_length_message"
              maxChars={240}
              isValid={messageCampaignStore.validationErrors.message.isValid}
              className="family-regular text-size-20 letter-spacing-5 full-height"
              onChange={messageChange}
            />
          </div>
          <ButtonBlock language={language} changeTextDirection={changeTextDirection} addSmile={addSmile} />
        </div>

        <MessageCampaignScheduled />
      </div>

      <div className="d-flex flex-row align-self-center gap-20">
        <Button title={localeStore.t('marketing_mc_save_btn')} onClick={handleSendCampaign} loading={messageCampaignStore.loading} size="large" />
        {messageCampaignStore.form.id ? (
          <Button
            title={localeStore.t('txt_delete')}
            onClick={handleDelete}
            loading={messageCampaignStore.loading}
            appearance="destructive-secondary"
            size="large"
          />
        ) : null}
      </div>
    </div>
  );
});

export default MessageCampaignForm;
